import React, { useEffect, useRef, useState } from 'react';
import { doc, updateDoc, onSnapshot, collection, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { DateTime } from 'luxon';
import { auth, db } from '../firebase';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  @keyframes messageIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .rasa-input:focus {
    border-color: #7c3aed !important;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.15) !important;
    outline: none !important;
  }
  .rasa-send-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .rasa-send-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(124,58,237,0.4) !important;
  }
  .rasa-intro-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .rasa-intro-btn:hover {
    transform: translateY(-1px) !important;
  }
`;

// ─────────────────────────────────────────────────────────────
// Timezone normalization, mirrored from functions/rasa/rasaLimits.js.
//
// Onboarding/settings has historically saved timezone values like
// "GMT+3" (from a manual offset picker), which Luxon does NOT
// recognize as a valid zone specifier — it only understands real IANA
// names ("Africa/Nairobi") or "UTC+3"/"+03:00" style offset strings.
// This MUST stay in sync with the backend's normalizeTimezone() —
// duplicated here (rather than shared) because this is a frontend
// file with no access to the functions/ directory at build time.
// If you ever change the backend version, update this one too.
// ─────────────────────────────────────────────────────────────
function normalizeTimezone(tz) {
  if (!tz || typeof tz !== 'string') return 'UTC';

  if (DateTime.now().setZone(tz).isValid) return tz;

  const match = tz.match(/^GMT([+-]\d{1,2}(:\d{2})?)$/i);
  if (match) {
    const converted = `UTC${match[1]}`;
    if (DateTime.now().setZone(converted).isValid) return converted;
  }

  return 'UTC';
}

// ─────────────────────────────────────────────────────────────
// FIRST-TIME INTRO
// Shown once — explains what Rasa is and isn't before the user's
// first message. Persisted via rasaIntroSeen on the user document.
// ─────────────────────────────────────────────────────────────
function RasaIntroModal({ onClose, theme, tr }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))',
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem',
        padding: '2rem',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: '1px solid rgba(192,132,252,0.2)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px', height: '64px', margin: '0 auto 1rem',
          background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.75rem',
          boxShadow: '0 8px 24px rgba(124,58,237,0.35)'
        }}>🪞</div>

        <h2 style={{ margin: 0, color: '#ffffff', fontSize: '1.3rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
          {tr('rasaIntroTitle', 'Meet Rasa')}
        </h2>

        <p style={{ margin: '0.875rem 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', lineHeight: 1.7 }}>
          {tr('rasaIntroDesc',
            "Rasa is a private space to reflect — just for you, separate from your accountability partner. It doesn't come in with assumptions about you or the world; it learns only from what you choose to share, and it's built to ask questions rather than hand you answers.")}
        </p>

        <div style={{
          marginTop: '1.25rem', padding: '0.875rem',
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '0.875rem', textAlign: 'left'
        }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
            {tr('rasaIntroNotTherapist',
              "Rasa isn't a therapist and isn't a replacement for real human connection — including your accountability partner. If something feels heavy, it'll gently point you toward support, not try to be it.")}
          </p>
        </div>

        <button
          className="rasa-intro-btn"
          onClick={onClose}
          style={{
            marginTop: '1.5rem', width: '100%', padding: '0.875rem',
            background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)',
            color: '#ffffff', border: 'none',
            borderRadius: '0.875rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem',
            boxShadow: '0 4px 15px rgba(124,58,237,0.35)'
          }}
        >
          {tr('rasaIntroStart', 'Begin')}
        </button>
      </div>
    </div>
  );
}

export default function RasaView({ user, userData, setUserData, activeVeylId, theme }) {
  const { t: translate } = useLanguage();

  const tr = (key, fallback) => {
    const val = translate(key);
    return (!val || val === key) ? fallback : val;
  };

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [liveVeylData, setLiveVeylData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const messagesEndRef = useRef(null);

  const sendingRef = useRef(false);

  // Sourced directly from the auth SDK's own state, not the `user` prop.
  // The prop is set once inside MainApp's onAuthStateChanged and then
  // threaded down through DashboardPage — reliable in the common case,
  // but for a strict, security-sensitive query like rasaConversations
  // (rule requires an EXACT uid match, no fallback clause), it's safer
  // to never depend on a value that travels through several layers of
  // props and renders. This listener guarantees authedUid is always
  // exactly what Firebase itself currently considers the signed-in user
  // at the moment the query is built — eliminating an entire class of
  // "prop technically correct but one tick stale" bugs, regardless of
  // whether that specific timing gap was the actual cause here.
  const [authedUid, setAuthedUid] = useState(() => auth.currentUser?.uid || null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setAuthedUid(firebaseUser?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  const dark = theme === 'dark';

  const colors = {
    cardBg:        dark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
    cardBorder:    dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    headerBg:      dark ? 'rgba(75,44,94,0.08)' : '#f9fafb',
    headerBorder:  dark ? 'rgba(192,132,252,0.12)' : '#e5e7eb',
    msgAreaBg:     dark ? 'rgba(0,0,0,0.15)' : '#f3f4f6',
    inputAreaBg:   dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
    inputBg:       dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
    inputBorder:   dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    inputColor:    dark ? '#ffffff' : '#1B2D44',
    text:          dark ? '#ffffff' : '#1B2D44',
    subtext:       dark ? 'rgba(255,255,255,0.45)' : '#6b7280',
    rasaMsgBg:     dark ? 'rgba(75,44,94,0.18)' : 'rgba(75,44,94,0.06)',
    rasaMsgBorder: dark ? 'rgba(192,132,252,0.15)' : 'rgba(192,132,252,0.2)',
    rasaMsgText:   dark ? '#ffffff' : '#1B2D44',
    emptyText:     dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    skeletonBg:    dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
  };

  useEffect(() => {
    if (userData && userData.rasaIntroSeen !== true) {
      setShowIntro(true);
    }
  }, [userData]);

  const dismissIntro = async () => {
    setShowIntro(false);
    try {
      await updateDoc(doc(db, 'users', user.uid), { rasaIntroSeen: true });
      setUserData(prev => ({ ...prev, rasaIntroSeen: true }));
    } catch (err) {
      console.error('Failed to persist rasaIntroSeen:', err);
    }
  };

  // Live veyl doc — source of truth for rasaMessagesUsedToday, same
  // pattern OverviewView.js uses for goalAchieved/status.
  useEffect(() => {
    if (!activeVeylId || typeof activeVeylId !== 'string') return;
    const unsubscribe = onSnapshot(
      doc(db, 'veyls', activeVeylId),
      (snap) => {
        if (snap.exists()) setLiveVeylData(snap.data());
      },
      (err) => {
        console.error('RasaView: veyl doc listener error:', err);
      }
    );
    return () => unsubscribe();
  }, [activeVeylId]);

  const FREE_DAILY_LIMIT = 10; // must match functions/rasa/rasaLimits.js

  // ─────────────────────────────────────────────────────────────
  // Day-rollover-aware remaining count.
  //
  // Previously this just read rasaMessagesUsedToday off the veyl doc
  // directly, with no awareness of whether a new day had started. The
  // backend (rasaLimits.js) correctly resets the counter on a new day,
  // but ONLY when rasaChat actually runs — and this component was
  // disabling the input/send button whenever the raw stored count hit
  // the limit, regardless of how stale that count was. Net effect: once
  // a user hit 10/10 one day, the button stayed disabled indefinitely
  // the next day too, because the disabled button prevented the very
  // call that would have triggered the backend's reset.
  //
  // Fix: mirror the backend's own day-comparison here so the frontend's
  // notion of "remaining" agrees with what the backend would compute if
  // called right now. This doesn't replace server-side enforcement —
  // rasaChat.js still recomputes and enforces this for real — it just
  // stops the frontend from blocking a legitimate call.
  // ─────────────────────────────────────────────────────────────
  const rasaUsed = (() => {
    if (!liveVeylData) return 0;

    const timezone = normalizeTimezone(userData?.timezone || liveVeylData?.timezone);
    const now = DateTime.now().setZone(timezone);
    const lastReset = liveVeylData.lastRasaReset
      ? DateTime.fromMillis(liveVeylData.lastRasaReset.toMillis()).setZone(timezone)
      : null;

    const isNewDay = !lastReset || !now.hasSame(lastReset, 'day');
    return isNewDay ? 0 : (liveVeylData.rasaMessagesUsedToday || 0);
  })();

  const rasaLeft = userData?.isPremium ? 999 : Math.max(0, FREE_DAILY_LIMIT - rasaUsed);

  // Conversation history — rasaConversations/{userId}/messages,
  // written server-side by rasaChat.js. Keyed off authedUid (live auth
  // state), not the user prop — see comment above authedUid's
  // declaration for why.
  useEffect(() => {
    if (!authedUid) return;
    setMessagesLoading(true);
    setMessagesError(null);
    const q = query(
      collection(db, 'rasaConversations', authedUid, 'messages'),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = [];
        snapshot.forEach(d => msgs.push({ id: d.id, ...d.data() }));
        setMessages(msgs);
        setMessagesLoading(false);
      },
      (err) => {
        console.error('RasaView: messages listener error:', err);
        setMessagesError(err.code || 'unknown-error');
        setMessagesLoading(false);
      }
    );
    return () => unsubscribe();
  }, [authedUid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const trimmed = newMessage.trim();
    if (!trimmed || sendingRef.current || !activeVeylId) return;
    if (rasaLeft <= 0) {
      setErrorMsg(tr('rasaDailyLimitReached', "You've reached today's Rasa limit. It resets tomorrow."));
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setErrorMsg(null);
    const messageToSend = trimmed;
    setNewMessage('');

    try {
      const functions = getFunctions();
      const rasaChat = httpsCallable(functions, 'rasaChat');
      await rasaChat({ veylId: activeVeylId, message: messageToSend });
    } catch (err) {
      setNewMessage(messageToSend);
      if (err.code === 'functions/resource-exhausted') {
        setErrorMsg(tr('rasaDailyLimitReached', "You've reached today's Rasa limit. It resets tomorrow."));
      } else {
        const errorLabel = tr('error', 'Error');
        setErrorMsg(`${errorLabel}: ${err.message}`);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <>
      <style>{styleTag}</style>

      {showIntro && <RasaIntroModal onClose={dismissIntro} theme={theme} tr={tr} />}

      <div style={{
        background: colors.cardBg,
        backdropFilter: 'blur(10px)',
        borderRadius: '1.25rem',
        border: `1px solid ${colors.cardBorder}`,
        display: 'flex', flexDirection: 'column',
        height: '600px',
        animation: 'fadeInUp 0.4s ease',
        overflow: 'hidden'
      }}>

        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${colors.headerBorder}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: colors.headerBg
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '38px', height: '38px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.15rem', flexShrink: 0
            }}>🪞</div>
            <div>
              <div style={{ fontWeight: '700', color: colors.text, fontSize: '0.95rem' }}>
                Rasa
              </div>
              <div style={{ fontSize: '0.78rem', color: colors.subtext, marginTop: '0.1rem' }}>
                {userData?.isPremium ? (
                  <span style={{ color: '#c084fc', fontWeight: '600' }}>
                    ✨ {tr('premiumUnlimited', 'Unlimited')}
                  </span>
                ) : (
                  <span>{rasaLeft} {tr('rasaMessagesLeft', 'reflections left today')}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', background: colors.msgAreaBg }}>
          {messagesError ? (
            <div style={{ textAlign: 'center', color: '#E05C5C', paddingTop: '3rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
              <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>
                {tr('rasaLoadError', "Couldn't load your conversation.")}
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: colors.subtext }}>
                ({messagesError}) — {tr('rasaLoadErrorDetail', 'check the browser console for details, or try refreshing.')}
              </p>
            </div>
          ) : messagesLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[{ width: '55%' }, { width: '65%' }, { width: '45%' }].map((item, i) => (
                <div key={i} style={{
                  height: '44px', width: item.width,
                  borderRadius: '1rem 1rem 1rem 0.25rem',
                  backgroundColor: colors.skeletonBg,
                  animation: 'pulse 1.6s ease-in-out infinite',
                  animationDelay: `${i * 0.1}s`
                }} />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.emptyText, paddingTop: '3rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🪞</div>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                {tr('rasaNoMessagesYet', 'Whenever you\u2019re ready to reflect, start typing below.')}
              </p>
            </div>
          ) : (
            messages.map(msg => {
              const isUser = msg.role === 'user';
              return (
                <div key={msg.id}
                  style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: '1rem', animation: 'messageIn 0.2s ease' }}>
                  <div style={{
                    maxWidth: '75%',
                    padding: '0.75rem 1rem',
                    borderRadius: isUser ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                    background: isUser ? 'linear-gradient(135deg, #4B2C5E, #7c3aed)' : colors.rasaMsgBg,
                    color: isUser ? '#ffffff' : colors.rasaMsgText,
                    border: isUser ? 'none' : `1px solid ${colors.rasaMsgBorder}`,
                    boxShadow: isUser ? '0 4px 12px rgba(124,58,237,0.25)' : 'none'
                  }}>
                    <span style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>{msg.text}</span>
                  </div>
                </div>
              );
            })
          )}
          {sending && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
              <div style={{
                padding: '0.75rem 1rem', borderRadius: '1rem 1rem 1rem 0.25rem',
                background: colors.rasaMsgBg, border: `1px solid ${colors.rasaMsgBorder}`,
                color: colors.subtext, fontSize: '0.85rem', fontStyle: 'italic'
              }}>
                {tr('rasaThinking', 'Rasa is reflecting…')}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Memory transparency note — static for now.
            TODO(Chat Topic 17): replace with real-time "I'm remembering X"
            events once memory extraction is actually implemented in
            rasaChat.js's extractMemoryUpdate(). Showing a live indicator
            before that exists would misrepresent what the app does. */}
        <div style={{ padding: '0.5rem 1.25rem', borderTop: `1px solid ${colors.cardBorder}` }}>
          <p style={{ fontSize: '0.72rem', color: colors.subtext, margin: 0, textAlign: 'center' }}>
            🔒 {tr('rasaPrivateNote', 'Private to you — never visible to your accountability partner.')}
          </p>
        </div>

        {errorMsg && (
          <div style={{ padding: '0.5rem 1.25rem', background: 'rgba(224,92,92,0.08)', borderTop: '1px solid rgba(224,92,92,0.2)' }}>
            <p style={{ fontSize: '0.78rem', color: '#E05C5C', margin: 0, textAlign: 'center' }}>{errorMsg}</p>
          </div>
        )}

        {/* Input area */}
        <form onSubmit={sendMessage} style={{ padding: '1rem 1.25rem', borderTop: `1px solid ${colors.headerBorder}`, background: colors.inputAreaBg }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input type="text" className="rasa-input" value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={rasaLeft <= 0 ? tr('rasaDailyLimitReached', "Today's limit reached") : tr('rasaPlaceholder', 'Share what\u2019s on your mind\u2026')}
              disabled={rasaLeft <= 0 || sending}
              style={{
                flex: 1, padding: '0.75rem 1rem',
                border: `1px solid ${colors.inputBorder}`,
                borderRadius: '0.75rem', fontSize: '0.95rem',
                backgroundColor: colors.inputBg, color: colors.inputColor,
                opacity: (rasaLeft <= 0 || sending) ? 0.5 : 1,
                boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s'
              }} />
            <button type="submit" className="rasa-send-btn"
              disabled={sending || !newMessage.trim() || rasaLeft <= 0}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)',
                color: '#FFFFFF', borderRadius: '0.75rem', fontWeight: '700', border: 'none',
                cursor: (sending || !newMessage.trim() || rasaLeft <= 0) ? 'not-allowed' : 'pointer',
                opacity: (sending || !newMessage.trim() || rasaLeft <= 0) ? 0.4 : 1,
                flexShrink: 0, boxShadow: '0 4px 15px rgba(124,58,237,0.3)', fontSize: '0.9rem'
              }}>
              {tr('send', 'Send')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}